import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Paper, Typography, Stack, Button, TextField, IconButton, Tooltip,
    Switch, FormControlLabel, Chip, CircularProgress, Breadcrumbs, Link as MuiLink,
    Table, TableHead, TableBody, TableRow, TableCell, TablePagination,
    InputAdornment, Alert, Divider, ThemeProvider,
    Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Swal from 'sweetalert2';
import api from '../../../services/api';
import { useNotify } from '../../../components/Datatable/NotifyProvider';
import { useAuth } from '../../../components/AuthProvider';
import { useLoading } from '../../../components/LoadingProvider';
import lightTheme from '../../../themes/lightTheme';
import {
    isValidDomainFormatRfcCompliant, isGovernmentDomain, isEssentialDomain,
    normalizeDomain, REASON_LABELS,
} from '../../../utils/domainFilter';

/**
 * Classifica uma lista de tokens em {valid, government, invalid, essential}.
 * `essential` é um sub-conjunto de `valid` — domínios que o backend ACEITA
 * mas que são sites conhecidos (Google, X, Facebook, etc). A UI alerta o
 * operador antes de salvar pra ele confirmar que sabe o que tá fazendo.
 */
function classifyDomains(rawTokens) {
    const valid = [];
    const government = [];
    const invalid = [];
    const essential = [];
    for (const token of rawTokens) {
        const norm = normalizeDomain(token);
        if (!norm) {
            invalid.push(token);
            continue;
        }
        if (isGovernmentDomain(norm)) {
            government.push(norm);
            continue;
        }
        if (!isValidDomainFormatRfcCompliant(norm)) {
            invalid.push(token);
            continue;
        }
        valid.push(norm);
        if (isEssentialDomain(norm)) essential.push(norm);
    }
    return { valid, government, invalid, essential };
}

/**
 * Página dedicada de categoria. Modos:
 * - id="new" → criação
 * - id=<int> → edição
 *
 * Toda a manipulação de domínios é LOCAL — adicionar, remover, restaurar
 * só altera o estado. Só na hora de Salvar manda 1 request com tudo via
 * POST /dns/blacklist/categories/save. Backend faz o diff.
 */
export default function CategoryDetails() {
    const { categoryId } = useParams();
    const isNew = categoryId === 'new';
    const navigate = useNavigate();
    const notify = useNotify();
    const auth = useAuth();
    const loading = useLoading();
    const { setLoading } = loading;

    // Estado da categoria
    const [meta, setMeta] = useState({
        name: '',
        description: '',
        active: true,
        expired: '',
        effective_from: '',
    });
    // Snapshot dos metadados ao abrir (ou após save) — usado pra detectar dirty
    const [initialMeta, setInitialMeta] = useState({
        name: '',
        description: '',
        active: true,
        expired: '',
        effective_from: '',
    });
    // Estado inicial dos domínios persistidos (vindo do DB) — usado pra diff visual
    const [initialDomains, setInitialDomains] = useState(new Set());
    // Estado final desejado (o que vai ser enviado) — vindo de cliques do usuário
    const [desired, setDesired] = useState(new Set());
    // Domínios marcados pra remover (visualmente riscados — vão sair do `desired`)
    // mas mantemos pra UI mostrar com Restore
    const [tombstones, setTombstones] = useState(new Set());

    // UI
    const [pageLoading, setPageLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState('');
    const [newDomain, setNewDomain] = useState('');
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [rejectedDetail, setRejectedDetail] = useState(null);  // dialog
    const [pasteModalOpen, setPasteModalOpen] = useState(false);
    const [pasteModalValue, setPasteModalValue] = useState('');

    // Classificação dos domínios do textarea com debounce — evita
    // re-classificar 10k tokens a cada keystroke (pode dar 100-300ms de lag).
    // Pra listas pequenas (<500), classifica na hora pra UX rápida.
    const [debouncedNewDomain, setDebouncedNewDomain] = useState(newDomain);
    useEffect(() => {
        const len = newDomain.length;
        const delay = len > 5000 ? 300 : len > 1000 ? 150 : 0;
        if (delay === 0) {
            setDebouncedNewDomain(newDomain);
            return;
        }
        const t = setTimeout(() => setDebouncedNewDomain(newDomain), delay);
        return () => clearTimeout(t);
    }, [newDomain]);

    const candidatesClassified = useMemo(() => {
        const tokens = debouncedNewDomain.split(/[,\s\n]+/).map(t => t.trim()).filter(Boolean);
        return { tokens, ...classifyDomains(tokens) };
    }, [debouncedNewDomain]);

    useEffect(() => {
        document.title = isNew ? 'Nova Categoria DNS' : `Categoria #${categoryId}`;
        if (isNew) return;

        let cancelled = false;
        setPageLoading(true);
        (async () => {
            try {
                const cat = await api.get(`/dns/blacklist/categories/${categoryId}`);
                if (cancelled) return;
                const data = cat.data || {};
                const loaded = {
                    name: data.name || '',
                    description: data.description || '',
                    active: !!data.active,
                    expired: data.expired ? new Date(data.expired).toISOString().slice(0, 10) : '',
                    effective_from: data.effective_from ? new Date(data.effective_from).toISOString().slice(0, 16) : '',
                };
                setMeta(loaded);
                setInitialMeta(loaded);

                // Busca TODOS os domínios — paginado client-side
                const all = await api.post('/dns/blacklist/domains', {
                    limit: 100000, offset: 0, query: '',
                }, { params: { category: categoryId } });
                const list = (all.data?.data || []).map(d => d.domain);
                const set = new Set(list);
                setInitialDomains(set);
                setDesired(new Set(set));
            } catch (e) {
                notify.setNotify({
                    open: true,
                    message: e.response?.data?.detail || 'Erro ao carregar categoria',
                    severity: 'error',
                });
            } finally {
                if (!cancelled) setPageLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [categoryId]);

    // Diff visual
    const added = useMemo(() => {
        const s = new Set();
        for (const d of desired) if (!initialDomains.has(d)) s.add(d);
        return s;
    }, [desired, initialDomains]);

    const removed = tombstones;  // simplificação: tombstones = removidos

    // Detecta se metadados mudaram em relação ao snapshot inicial
    const metaDirty = useMemo(() => {
        return (
            meta.name !== initialMeta.name ||
            meta.description !== initialMeta.description ||
            !!meta.active !== !!initialMeta.active ||
            (meta.expired || '') !== (initialMeta.expired || '') ||
            (meta.effective_from || '') !== (initialMeta.effective_from || '')
        );
    }, [meta, initialMeta]);

    // Tem mudanças reais pra salvar?
    // - Criação: precisa ter nome
    // - Edição: nome preenchido + (metadados mudaram OU mexeu em domínios)
    const hasChanges = isNew
        ? meta.name.trim().length > 0
        : (meta.name.trim().length > 0 && (metaDirty || added.size > 0 || removed.size > 0));

    // ===== Operações locais sobre domínios =====

    const addDomain = (raw) => {
        const candidate = normalizeDomain(raw);
        if (!candidate) return;
        // Validação client-side de RFC + gov (mostra warning, mas backend é a fonte da verdade)
        if (!isValidDomainFormatRfcCompliant(candidate)) {
            notify.setNotify({ open: true, message: `"${raw}" não é um domínio válido`, severity: 'warning' });
            return;
        }
        if (isGovernmentDomain(candidate)) {
            notify.setNotify({ open: true, message: `Domínios governamentais não podem ser bloqueados (${candidate})`, severity: 'warning' });
            return;
        }
        if (desired.has(candidate)) {
            notify.setNotify({ open: true, message: 'Domínio já está na lista', severity: 'info' });
            return;
        }
        setDesired(prev => { const n = new Set(prev); n.add(candidate); return n; });
        // Se estava tombstone (removido antes), tira da lista de tombstones
        setTombstones(prev => { const n = new Set(prev); n.delete(candidate); return n; });
        setNewDomain('');
    };

    const addBulk = (raw) => {
        const tokens = raw.split(/[,\s\n]+/).map(t => t.trim()).filter(Boolean);
        let okN = 0, badN = 0;
        const next = new Set(desired);
        const nextTomb = new Set(tombstones);
        for (const t of tokens) {
            const c = normalizeDomain(t);
            if (!c || !isValidDomainFormatRfcCompliant(c) || isGovernmentDomain(c)) {
                badN++;
                continue;
            }
            next.add(c);
            nextTomb.delete(c);
            okN++;
        }
        setDesired(next);
        setTombstones(nextTomb);
        notify.setNotify({
            open: true,
            message: `${okN} adicionado(s)${badN > 0 ? ` · ${badN} ignorado(s)` : ''}`,
            severity: okN > 0 ? 'success' : 'warning',
        });
    };

    const removeDomain = (domain) => {
        setDesired(prev => { const n = new Set(prev); n.delete(domain); return n; });
        // Se estava no DB → marca tombstone pra mostrar visualmente
        if (initialDomains.has(domain)) {
            setTombstones(prev => { const n = new Set(prev); n.add(domain); return n; });
        }
    };

    const restoreDomain = (domain) => {
        setDesired(prev => { const n = new Set(prev); n.add(domain); return n; });
        setTombstones(prev => { const n = new Set(prev); n.delete(domain); return n; });
    };

    // Importar arquivo
    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const text = await file.text();
        addBulk(text);
        e.target.value = '';
    };

    // ===== Tabela: filtragem + paginação =====
    const allRows = useMemo(() => {
        // União de desired + tombstones (pra mostrar removidos com Restore)
        const set = new Set([...desired, ...tombstones]);
        return [...set].sort();
    }, [desired, tombstones]);

    // Contadores totais por categoria — pros chips de filtro
    const counts = useMemo(() => {
        let gov = 0, inv = 0, ess = 0;
        for (const d of desired) {
            if (isGovernmentDomain(d)) gov++;
            else if (!isValidDomainFormatRfcCompliant(d)) inv++;
            else if (isEssentialDomain(d)) ess++;
        }
        return {
            new: added.size,
            current: desired.size - added.size,
            removing: tombstones.size,
            government: gov,
            invalid: inv,
            essential: ess,
        };
    }, [desired, added, tombstones]);

    const [filterMode, setFilterMode] = useState('all');
    // all | new | current | removing | invalid | government | essential

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        let base = allRows;
        switch (filterMode) {
            case 'new':
                base = base.filter(d => added.has(d));
                break;
            case 'current':
                base = base.filter(d => !added.has(d) && !tombstones.has(d));
                break;
            case 'removing':
                base = base.filter(d => tombstones.has(d));
                break;
            case 'invalid':
                base = base.filter(d => !isValidDomainFormatRfcCompliant(d) && !isGovernmentDomain(d));
                break;
            case 'government':
                base = base.filter(d => isGovernmentDomain(d));
                break;
            case 'essential':
                base = base.filter(d => isEssentialDomain(d) && !isGovernmentDomain(d) && isValidDomainFormatRfcCompliant(d));
                break;
            default:
                break;
        }
        if (!q) return base;
        return base.filter(d => d.toLowerCase().includes(q));
    }, [allRows, query, filterMode, added, tombstones]);

    const pageRows = filtered.slice(page * perPage, page * perPage + perPage);

    // ===== Salvar =====

    const handleSave = async () => {
        if (!meta.name.trim()) {
            notify.setNotify({ open: true, message: 'Nome da categoria é obrigatório', severity: 'warning' });
            return;
        }

        // Sub-confirmação: sites grandes/conhecidos NOVOS sendo bloqueados
        const essentialBeingAdded = [...added].filter(d => isEssentialDomain(d));
        if (essentialBeingAdded.length > 0) {
            const preview = essentialBeingAdded.slice(0, 10).join('\n');
            const remaining = essentialBeingAdded.length - 10;
            const r = await Swal.fire({
                title: '⚠ Sites grandes na lista',
                html: `Você está prestes a bloquear <strong>${essentialBeingAdded.length} site(s) amplamente usado(s)</strong>. ` +
                      `Isso pode impactar muitos usuários da rede.<br><br>` +
                      `<pre style="text-align:left;background:#f5f5f5;padding:8px;border-radius:4px;font-size:12px;max-height:200px;overflow:auto">` +
                      `${preview}${remaining > 0 ? `\n… e mais ${remaining}` : ''}</pre><br>` +
                      `Tem certeza que quer continuar?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sim, bloquear mesmo assim',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#ed6c02',
            });
            if (!r.isConfirmed) return;
        }

        const summaryParts = [];
        if (added.size === 1) summaryParts.push('1 domínio novo');
        else if (added.size > 1) summaryParts.push(`${added.size} domínios novos`);
        if (removed.size === 1) summaryParts.push('1 pra remover');
        else if (removed.size > 1) summaryParts.push(`${removed.size} pra remover`);

        if (!isNew && summaryParts.length > 0) {
            const r = await Swal.fire({
                title: 'Confirmar alterações?',
                text: `Você está aplicando: ${summaryParts.join(', ')}. Os servidores de DNS vão ser atualizados em seguida.`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sim, salvar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#2e7d32',
            });
            if (!r.isConfirmed) return;
        }

        setSaving(true);
        try {
            const body = {
                id: isNew ? null : Number(categoryId),
                name: meta.name.trim(),
                description: meta.description.trim() || null,
                active: meta.active,
                expired: meta.expired ? new Date(meta.expired).toISOString() : null,
                effective_from: meta.effective_from ? new Date(meta.effective_from).toISOString() : null,
                domains: [...desired],
            };
            const res = await api.post('/dns/blacklist/categories/save', body);
            const data = res.data || {};

            if (data.task_id) {
                try { window.dispatchEvent(new Event('task-enqueued')); } catch (_) {}
            }
            const parts = [
                isNew ? 'Categoria criada' : 'Categoria atualizada',
                data.added ? `${data.added} domínio(s) novo(s)` : null,
                data.removed ? `${data.removed} removido(s)` : null,
                data.reactivated ? `${data.reactivated} reativado(s)` : null,
                data.rejected?.length ? `${data.rejected.length} rejeitado(s)` : null,
                data.task_id ? 'atualizando servidores DNS' : null,
            ].filter(Boolean).join(' · ');
            notify.setNotify({ open: true, message: parts, severity: 'success' });

            // Se criou, navega pra página de edição da nova categoria
            if (isNew && data.id) {
                navigate(`/dns_blacklist/category/${data.id}`, { replace: true });
            } else {
                // Reseta tombstones (foi persistido), atualiza snapshots
                setInitialDomains(new Set(desired));
                setTombstones(new Set());
                setInitialMeta({ ...meta });
            }
        } catch (e) {
            notify.setNotify({
                open: true,
                message: e.response?.data?.detail || 'Erro ao salvar',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (isNew) return;
        const r = await Swal.fire({
            title: 'Apagar categoria?',
            text: 'Soft-delete — preserva histórico, mas remove a categoria do bloqueio.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Apagar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#aa2525',
        });
        if (!r.isConfirmed) return;
        try {
            await api.delete(`/dns/blacklist/categories/${categoryId}`);
            try { window.dispatchEvent(new Event('task-enqueued')); } catch (_) {}
            notify.setNotify({ open: true, message: 'Categoria apagada', severity: 'success' });
            navigate('/dns_blacklist');
        } catch (e) {
            notify.setNotify({
                open: true,
                message: e.response?.data?.detail || 'Erro ao apagar',
                severity: 'error',
            });
        }
    };

    if (pageLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <ThemeProvider theme={lightTheme}>
        <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: 'calc(100vh - 100px)', p: { xs: 1, md: 2 } }}>
            {/* Header */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <MuiLink component="button" onClick={() => navigate('/dns_blacklist')}>
                    DNS Blacklist
                </MuiLink>
                <Typography color="text.primary">
                    {isNew ? 'Nova categoria' : meta.name || `Categoria #${categoryId}`}
                </Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/dns_blacklist')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" sx={{ fontFamily: 'Montserrat', fontWeight: 300 }}>
                        {isNew ? 'Nova Categoria' : meta.name}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                    {!isNew && (
                        <Button color="error" startIcon={<DeleteIcon />} onClick={handleDelete} disabled={saving}>
                            Apagar
                        </Button>
                    )}
                    <Tooltip title={!hasChanges ? (isNew ? 'Informe um nome' : 'Nenhuma alteração pra salvar') : ''}>
                        <span>
                            <Button variant="contained" color="primary" startIcon={<SaveIcon />}
                                disabled={saving || !hasChanges} onClick={handleSave}>
                                {saving ? <CircularProgress size={18} /> : (isNew ? 'Criar' : 'Salvar')}
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Indicador único de alterações pendentes (sempre logo abaixo do header) */}
            {!isNew && (added.size > 0 || removed.size > 0 || metaDirty) && (() => {
                const parts = [];
                if (added.size === 1) parts.push('1 domínio novo');
                else if (added.size > 1) parts.push(`${added.size} domínios novos`);
                if (removed.size === 1) parts.push('1 pra remover');
                else if (removed.size > 1) parts.push(`${removed.size} pra remover`);
                if (metaDirty) parts.push('configuração alterada');
                return (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Você tem alterações pendentes ({parts.join(', ')}).
                        Clique em <strong>Salvar</strong> pra aplicar.
                    </Alert>
                );
            })()}

            {/* Metadados */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle2">Configuração</Typography>
                    {/* Switch ativa/inativa destacado no canto do card */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        bgcolor: meta.active ? 'rgba(46,125,50,0.08)' : 'rgba(0,0,0,0.04)',
                        px: 1.5, py: 0.5, borderRadius: 1,
                        border: '1px solid',
                        borderColor: meta.active ? 'success.main' : 'divider',
                    }}>
                        <Switch
                            checked={meta.active}
                            onChange={(e) => setMeta({ ...meta, active: e.target.checked })}
                            color="success"
                        />
                        <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: meta.active ? 'success.main' : 'text.secondary',
                        }}>
                            {meta.active ? 'Categoria ATIVA' : 'Categoria INATIVA'}
                        </Typography>
                    </Box>
                </Box>
                <Stack spacing={2}>
                    <TextField
                        label="Nome"
                        value={meta.name}
                        onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                        size="small"
                        fullWidth
                        required
                    />
                    <TextField
                        label="Descrição"
                        value={meta.description}
                        onChange={(e) => setMeta({ ...meta, description: e.target.value })}
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                    />
                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                        <TextField
                            label="Expira em"
                            type="date"
                            value={meta.expired}
                            onChange={(e) => setMeta({ ...meta, expired: e.target.value })}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            helperText="Vazio = não expira"
                            sx={{ minWidth: 200 }}
                        />
                        <TextField
                            label="Entra em vigor em"
                            type="datetime-local"
                            value={meta.effective_from}
                            onChange={(e) => setMeta({ ...meta, effective_from: e.target.value })}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            helperText="Vazio = vigor imediato"
                            sx={{ minWidth: 240 }}
                        />
                    </Stack>
                </Stack>
            </Paper>

            {/* Domínios */}
            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2">Domínios</Typography>
                        <Chip size="small" label={`${desired.size} no total`} variant="outlined" />
                        {added.size > 0 && <Chip size="small" label={`+${added.size}`} color="success" />}
                        {removed.size > 0 && <Chip size="small" label={`-${removed.size}`} color="error" />}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button component="label" size="small" startIcon={<UploadFileIcon />}>
                            Importar arquivo
                            <input type="file" hidden accept=".txt,.csv" onChange={handleFile} />
                        </Button>
                    </Stack>
                </Box>

                {/* Add bar — textarea multiline pra colar lista inteira */}
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Adicionar domínios <strong>(cole uma lista inteira — 1 por linha, vírgula ou espaço)</strong>
                        </Typography>
                        <Tooltip title="Abrir em janela maior pra colar/editar listas grandes">
                            <IconButton size="small" onClick={() => {
                                setPasteModalValue(newDomain);
                                setPasteModalOpen(true);
                            }}>
                                <OpenInFullIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <TextField
                        size="small"
                        multiline
                        rows={3}
                        placeholder={'evil.com\nphishing.example.org\nbad.site\n\n(Cole uma lista grande aqui ou use o botão de expandir →)'}
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        fullWidth
                        sx={{
                            bgcolor: 'background.paper',
                            '& .MuiInputBase-root': { maxHeight: 200, overflow: 'auto' },
                        }}
                    />
                    {/* Classificação ao vivo: válidos + rejeitados */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {candidatesClassified.tokens.length === 0 ? (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Cole ou digite os domínios acima
                                </Typography>
                            ) : (
                                <>
                                    <Chip
                                        size="small"
                                        label={`${candidatesClassified.valid.length} válido(s)`}
                                        color="success"
                                        variant="outlined"
                                    />
                                    {candidatesClassified.essential.length > 0 && (
                                        <Chip
                                            size="small"
                                            label={`⚠ ${candidatesClassified.essential.length} site(s) conhecido(s)`}
                                            color="warning"
                                            variant="filled"
                                            onClick={() => setRejectedDetail({
                                                title: 'Sites grandes/conhecidos detectados',
                                                items: candidatesClassified.essential,
                                                reason: 'essential',
                                            })}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    )}
                                    {candidatesClassified.government.length > 0 && (
                                        <Chip
                                            size="small"
                                            label={`${candidatesClassified.government.length} governamental(is)`}
                                            color="warning"
                                            variant="outlined"
                                            onClick={() => setRejectedDetail({
                                                title: 'Domínios governamentais rejeitados',
                                                items: candidatesClassified.government,
                                                reason: 'government',
                                            })}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    )}
                                    {candidatesClassified.invalid.length > 0 && (
                                        <Chip
                                            size="small"
                                            label={`${candidatesClassified.invalid.length} inválido(s)`}
                                            color="error"
                                            variant="outlined"
                                            onClick={() => setRejectedDetail({
                                                title: 'Domínios inválidos rejeitados',
                                                items: candidatesClassified.invalid,
                                                reason: 'invalid',
                                            })}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    )}
                                </>
                            )}
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" onClick={() => setNewDomain('')} disabled={!newDomain}>
                                Limpar
                            </Button>
                            <Button
                                variant="contained" size="small" startIcon={<AddIcon />}
                                disabled={candidatesClassified.valid.length === 0}
                                onClick={() => {
                                    // Já filtrado: só envia os válidos (silenciosamente,
                                    // pq o user já vê a classificação acima)
                                    const next = new Set(desired);
                                    const nextTomb = new Set(tombstones);
                                    for (const v of candidatesClassified.valid) {
                                        next.add(v);
                                        nextTomb.delete(v);
                                    }
                                    setDesired(next);
                                    setTombstones(nextTomb);
                                    notify.setNotify({
                                        open: true,
                                        message: `${candidatesClassified.valid.length} adicionado(s)`
                                            + (candidatesClassified.government.length || candidatesClassified.invalid.length
                                                ? ` · ${candidatesClassified.government.length + candidatesClassified.invalid.length} rejeitado(s)`
                                                : ''),
                                        severity: 'success',
                                    });
                                    setNewDomain('');
                                }}
                            >
                                Adicionar {candidatesClassified.valid.length > 1 ? `(${candidatesClassified.valid.length})` : ''}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>

                {/* Search */}
                <TextField
                    size="small"
                    placeholder="Buscar domínio na lista…"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setPage(0); }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    }}
                    fullWidth
                    sx={{ mb: 1.5 }}
                />

                {/* Filtros por tipo — chips clicáveis */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                    {[
                        { id: 'all',         label: 'Todos',           count: allRows.length, color: 'primary' },
                        { id: 'new',         label: 'Novos',           count: counts.new,         color: 'success' },
                        { id: 'current',     label: 'Atuais',          count: counts.current,     color: 'default' },
                        { id: 'removing',    label: 'A remover',       count: counts.removing,    color: 'error' },
                        { id: 'essential',   label: 'Sites grandes',   count: counts.essential,   color: 'warning' },
                        { id: 'government',  label: 'Governamentais',  count: counts.government,  color: 'warning' },
                        { id: 'invalid',     label: 'Inválidos',       count: counts.invalid,     color: 'error' },
                    ].filter(f => f.id === 'all' || f.count > 0).map(f => (
                        <Chip
                            key={f.id}
                            label={`${f.label} (${f.count})`}
                            size="small"
                            color={filterMode === f.id ? f.color : 'default'}
                            variant={filterMode === f.id ? 'filled' : 'outlined'}
                            onClick={() => { setFilterMode(filterMode === f.id ? 'all' : f.id); setPage(0); }}
                            clickable
                        />
                    ))}
                </Box>


                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Domínio</TableCell>
                            <TableCell sx={{ width: 120 }}>Status</TableCell>
                            <TableCell align="right" sx={{ width: 80 }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                                    {desired.size === 0 ? 'Nenhum domínio. Adicione acima.' : 'Sem resultados pra essa busca.'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            pageRows.map((d) => {
                                const isAdded = added.has(d);
                                const isRemoved = removed.has(d);
                                // Detecta legados que não passariam pelo filtro atual
                                const isInvalid = !isValidDomainFormatRfcCompliant(d);
                                const isGov = isGovernmentDomain(d);
                                const isEss = !isGov && !isInvalid && isEssentialDomain(d);
                                return (
                                    <TableRow key={d} hover sx={{
                                        bgcolor: isAdded ? 'rgba(46,125,50,0.06)' : (isRemoved ? 'rgba(170,37,37,0.06)' : 'transparent'),
                                    }}>
                                        <TableCell sx={{
                                            fontFamily: 'monospace',
                                            textDecoration: isRemoved ? 'line-through' : 'none',
                                            color: isRemoved ? 'text.disabled' : 'text.primary',
                                        }}>
                                            {d}
                                            {(isInvalid || isGov) && !isRemoved && (
                                                <Tooltip title={isGov ? 'Domínio governamental — não deveria estar bloqueado' : 'Formato inválido pelo filtro atual'}>
                                                    <ErrorIcon
                                                        fontSize="small"
                                                        color={isGov ? 'warning' : 'error'}
                                                        sx={{ ml: 1, verticalAlign: 'middle' }}
                                                    />
                                                </Tooltip>
                                            )}
                                            {isEss && !isRemoved && (
                                                <Tooltip title="Site grande/conhecido — impacta muitos usuários">
                                                    <ErrorIcon fontSize="small" color="warning" sx={{ ml: 1, verticalAlign: 'middle' }} />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {isAdded && <Chip label="Novo" size="small" color="success" variant="outlined" />}
                                            {isRemoved && <Chip label="A remover" size="small" color="error" variant="outlined" />}
                                            {!isAdded && !isRemoved && !isInvalid && !isGov && !isEss && <Chip label="Atual" size="small" variant="outlined" />}
                                            {!isAdded && !isRemoved && isGov && <Chip label="Governamental" size="small" color="warning" variant="outlined" />}
                                            {!isAdded && !isRemoved && isInvalid && !isGov && <Chip label="Inválido" size="small" color="error" variant="outlined" />}
                                            {!isAdded && !isRemoved && isEss && <Chip label="Site grande" size="small" color="warning" variant="outlined" />}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isRemoved ? (
                                                <Tooltip title="Desfazer remoção">
                                                    <IconButton size="small" onClick={() => restoreDomain(d)}>
                                                        <RestoreIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Remover">
                                                    <IconButton size="small" color="error" onClick={() => removeDomain(d)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filtered.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => { setPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    labelRowsPerPage="Por página:"
                />
            </Paper>

            {/* Modal pra colar lista grande em tela cheia */}
            <Dialog
                open={pasteModalOpen}
                onClose={() => setPasteModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Colar lista de domínios</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                        Cole sua lista aqui (1 por linha, vírgula ou espaço).
                        Você vê a classificação ao vivo no rodapé.
                    </Typography>
                    <TextField
                        autoFocus
                        multiline
                        rows={18}
                        placeholder={'evil.com\nphishing.example.org\nbad.site\nfraude.exemplo\n…'}
                        value={pasteModalValue}
                        onChange={(e) => setPasteModalValue(e.target.value)}
                        fullWidth
                        sx={{ fontFamily: 'monospace' }}
                    />
                    {(() => {
                        const tokens = pasteModalValue.split(/[,\s\n]+/).map(t => t.trim()).filter(Boolean);
                        const cls = classifyDomains(tokens);
                        if (tokens.length === 0) return null;
                        return (
                            <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                                <Chip size="small" color="success" variant="outlined"
                                    label={`${cls.valid.length} válido(s)`} />
                                {cls.essential.length > 0 && (
                                    <Chip size="small" color="warning" variant="filled"
                                        label={`⚠ ${cls.essential.length} site(s) conhecido(s)`} />
                                )}
                                {cls.government.length > 0 && (
                                    <Chip size="small" color="warning" variant="outlined"
                                        label={`${cls.government.length} governamental(is)`} />
                                )}
                                {cls.invalid.length > 0 && (
                                    <Chip size="small" color="error" variant="outlined"
                                        label={`${cls.invalid.length} inválido(s)`} />
                                )}
                            </Stack>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPasteModalValue(''); }}>Limpar tudo</Button>
                    <Button onClick={() => setPasteModalOpen(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            // Copia o conteúdo do modal pro textarea principal
                            setNewDomain(pasteModalValue);
                            setPasteModalOpen(false);
                        }}
                        disabled={!pasteModalValue.trim()}
                    >
                        Usar essa lista
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog detalhando rejeitados do textarea */}
            <Dialog
                open={!!rejectedDetail}
                onClose={() => setRejectedDetail(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{rejectedDetail?.title}</DialogTitle>
                <DialogContent dividers>
                    {rejectedDetail?.reason === 'essential' ? (
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                            Esses domínios <strong>vão ser adicionados normalmente</strong>, mas são
                            sites grandes e amplamente usados. Bloquear pode impactar muitos usuários.
                            Confirme com sua equipe antes de salvar.
                        </Alert>
                    ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                            Esses domínios <strong>não vão ser adicionados</strong> ao clicar em Adicionar.
                            Razão: {REASON_LABELS[rejectedDetail?.reason] || '—'}.
                        </Typography>
                    )}
                    <List dense sx={{ maxHeight: 320, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
                        {(rejectedDetail?.items || []).map((d, i) => (
                            <ListItem key={i}>
                                <ListItemText
                                    primary={d}
                                    primaryTypographyProps={{ fontFamily: 'monospace', fontSize: 13 }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectedDetail(null)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
        </ThemeProvider>
    );
}
